import { Sun, Moon, Monitor } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { NewsletterSignupWidget } from '@/features/mailing-list'
import { useAuth } from '@/hooks/useAuth'
import { useTheme } from '@/context/ThemeContext'
import { useEmailPreferences } from '../hooks/useEmailPreferences'
import type { MailingListSegment } from '@/types'

const SEGMENTS: { value: MailingListSegment; label: string; description: string }[] = [
  { value: 'general', label: 'General', description: 'News, announcements, and updates from ODD' },
  { value: 'film-updates', label: 'Film Updates', description: 'New episodes, seasons, and film releases' },
  { value: 'services', label: 'Services', description: 'Service offerings and promotions' },
  { value: 'oddfest', label: 'ODDFEST', description: 'ODDFEST event announcements and tickets' },
  { value: 'shift', label: 'S.H.I.F.T.', description: 'S.H.I.F.T. program updates and opportunities' },
]

function getInitials(name: string | null | undefined, email: string | null | undefined): string {
  if (name) {
    const parts = name.trim().split(' ')
    return parts.length >= 2
      ? `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase()
      : (parts[0]?.[0] ?? '?').toUpperCase()
  }
  return (email?.[0] ?? '?').toUpperCase()
}

export function SettingsPage() {
  const { user } = useAuth()
  const { theme, setTheme } = useTheme()
  const { member, loading, saving, error, updateSegments, unsubscribeAll, resubscribeAll } =
    useEmailPreferences()

  function handleSegmentToggle(value: MailingListSegment) {
    if (!member) return
    const next = member.segments.includes(value)
      ? member.segments.filter((s) => s !== value)
      : [...member.segments, value]
    void updateSegments(next)
  }

  return (
    <div className="container mx-auto max-w-2xl px-4 py-12">
      <h1 className="mb-8 text-3xl font-bold">Settings</h1>

      <Tabs defaultValue="profile">
        <TabsList className="mb-6">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="email">Email Preferences</TabsTrigger>
        </TabsList>

        {/* ── Profile Tab ──────────────────────────────────────────── */}
        <TabsContent value="profile" className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={user?.photoURL ?? undefined} alt={user?.displayName ?? ''} />
              <AvatarFallback className="text-lg">
                {getInitials(user?.displayName, user?.email)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-lg font-semibold">{user?.displayName ?? 'No name set'}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <Separator />
          <p className="text-sm text-muted-foreground">
            To update your name or profile photo, contact an administrator.
          </p>
          <Separator />
          <div className="space-y-3">
            <p className="font-medium">Appearance</p>
            <p className="text-sm text-muted-foreground">Choose your preferred color theme.</p>
            <div className="grid grid-cols-3 gap-3">
              {(
                [
                  { value: 'light', label: 'Light', Icon: Sun },
                  { value: 'system', label: 'System', Icon: Monitor },
                  { value: 'dark', label: 'Dark', Icon: Moon },
                ] as const
              ).map(({ value, label, Icon }) => (
                <button
                  key={value}
                  onClick={() => setTheme(value)}
                  className={`flex flex-col items-center gap-2 rounded-md border p-4 text-sm font-medium transition-colors hover:bg-muted/50 ${
                    theme === value
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'text-muted-foreground'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* ── Email Preferences Tab ─────────────────────────────────── */}
        <TabsContent value="email" className="space-y-6">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : member === null ? (
            /* Not on the mailing list yet */
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                You're not currently on our mailing list. Sign up below to receive updates from ODD.
              </p>
              <div className="max-w-xs">
                <NewsletterSignupWidget />
              </div>
            </div>
          ) : (
            /* Subscribed — show preferences */
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email Subscription</p>
                  <p className="text-sm text-muted-foreground">
                    {member.isSubscribed
                      ? 'You are currently subscribed.'
                      : 'You have unsubscribed from all emails.'}
                  </p>
                </div>
                <Badge variant={member.isSubscribed ? 'default' : 'secondary'}>
                  {member.isSubscribed ? 'Subscribed' : 'Unsubscribed'}
                </Badge>
              </div>

              {member.isSubscribed && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <p className="font-medium">Email Lists</p>
                    <p className="text-sm text-muted-foreground">
                      Choose which updates you'd like to receive.
                    </p>
                    <div className="space-y-3">
                      {SEGMENTS.map(({ value, label, description }) => {
                        const checked = member.segments.includes(value)
                        return (
                          <label
                            key={value}
                            className="flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-muted/50"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={saving}
                              onChange={() => handleSegmentToggle(value)}
                              className="mt-0.5 h-4 w-4 shrink-0 rounded border-border accent-primary"
                            />
                            <div>
                              <p className="text-sm font-medium leading-none">{label}</p>
                              <p className="mt-1 text-xs text-muted-foreground">{description}</p>
                            </div>
                          </label>
                        )
                      })}
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {error && <p className="text-sm text-destructive">{error}</p>}

              <div>
                {member.isSubscribed ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void unsubscribeAll()}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Unsubscribe from all emails'}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void resubscribeAll()}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Re-subscribe to emails'}
                  </Button>
                )}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
